import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { apiReports } from "../api/reports";
import SchedulerTextBreakdown, { schedulerTextLabels } from "./SchedulerTextBreakdown";
import { monitoringDateLabel, monitoringDuration, monitoringMarkers, monitoringStart, monitoringToday, shiftMonitoringDate } from "./timeMonitoringUtils";

// Compact scheduler activity header; gated server-side by role, entitlement and feature flag.
const hours = (v) => `${Number(v || 0).toFixed(1)}h`;
const engagedTime = (v) => Number(v || 0) < 1 ? `${Math.round(Math.max(0, Number(v) || 0) * 60)}m` : hours(v);
const clockDuration = (ms) => {
  const minutes = Math.max(0, Math.round((Number(ms) || 0) / 60000));
  return minutes < 60 ? `${minutes}m` : `${Math.floor(minutes / 60)}h${minutes % 60 ? ` ${minutes % 60}m` : ""}`;
};
// Server totals union today's authenticated sessions; logout gaps never advance this clock.
// Activity without authoritative session timing falls back to the previous clock,
// elapsed since first recorded activity of the day.
function loggedInSpan(person, asOf) {
  const seconds = person.signedInSeconds;
  if (typeof seconds === "number" && Number.isFinite(seconds) && seconds >= 0) {return { ms: seconds * 1000 };}
  const first = person.loggedInAt && new Date(person.loggedInAt).getTime();
  const until = asOf?.getTime();
  if (Number.isFinite(first) && Number.isFinite(until) && until >= first) {return { ms: until - first, fallback: true };}
  return null;
}
const timeET = (d) => d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" });
const etActivityDate = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York", weekday: "short", year: "numeric", month: "2-digit", day: "2-digit",
});
const button = "tw-rounded tw-px-3 tw-py-2 tw-text-sm focus:tw-ring-2 focus:tw-ring-blue-500";
// Same category colours as the Time Monitoring report.
const timelineCategoryColors = {
  scheduling: "#2563eb", review: "#16a34a", home: "#9ca3af", other: "#f59e0b",
};
function Cookie({ person }) {
  const fill = Math.min(1, Math.max(0, Number(person.engagedHours) || 0) / 12);
  const online = person.online === true;
  const color = online ? "#16a34a" : "#94a3b8";
  const remainder = online ? "#dcfce7" : "#e2e8f0";
  return <span role="img" aria-label={`${engagedTime(person.engagedHours)} of 12 engaged hours; ${Math.round(fill * 100)}% filled`}
    title={`${engagedTime(person.engagedHours)} engaged · full cookie = 12 hours`}
    className={`tw-inline-flex tw-flex-shrink-0 tw-w-8 tw-h-8 tw-rounded-full tw-border ${online ? "tw-border-green-600" : "tw-border-gray-300"}`}
    style={{
      background: `conic-gradient(${color} ${fill * 360}deg, ${remainder} 0deg)`,
      boxShadow: online ? "0 0 0 2px #bbf7d0" : "none",
    }}>
    <svg viewBox="0 0 32 32" aria-hidden="true" className="tw-w-full tw-h-full" fill="#655141" fillOpacity="0.65">
      <circle cx="11" cy="7" r="1.7" /><circle cx="23" cy="10" r="2" /><circle cx="7" cy="17" r="1.5" />
      <circle cx="17" cy="16" r="2.2" /><circle cx="24" cy="23" r="1.5" /><circle cx="13" cy="25" r="1.8" />
      <circle cx="18" cy="5" r="0.8" /><circle cx="5" cy="11" r="0.7" /><circle cx="21" cy="28" r="0.7" />
    </svg>
  </span>;
}

function schedulerDisplayName(person) {
  return person.nickname?.trim() || person.name;
}

function presenceLabel(person, asOf) {
  if (person.online == null) {return "Activity unavailable";}
  const timestamp = person.online ? person.onlineSince : person.signedOutAt || person.lastActivityAt;
  const at = new Date(timestamp).getTime();
  const now = asOf?.getTime();
  if (!timestamp || !Number.isFinite(at) || !Number.isFinite(now) || at > now) {
    return person.online ? "Online" : "Offline";
  }
  if (!person.online) {
    const dayFormat = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York", weekday: "short", year: "numeric", month: "numeric", day: "numeric",
    });
    const parts = (date) => Object.fromEntries(dayFormat.formatToParts(date).map(({ type, value }) => [type, value]));
    const seen = parts(new Date(at));
    const today = parts(new Date(now));
    const dayOffset = (Date.UTC(today.year, today.month - 1, today.day) - Date.UTC(seen.year, seen.month - 1, seen.day)) / 86400000;
    if (dayOffset > 7) {
      const day = Number(seen.day);
      const suffix = day % 100 >= 11 && day % 100 <= 13 ? "th" : ["th", "st", "nd", "rd"][day % 10] || "th";
      return `Offline ${day}${suffix}`;
    }
    if (dayOffset > 0) {return `Offline ${seen.weekday}`;}
  }
  const minutes = Math.floor((now - at) / 60000);
  let duration = minutes ? `${minutes}m` : "<1m";
  if (minutes >= 60) {duration = `${Math.floor(minutes / 60)}h${minutes % 60 ? ` ${minutes % 60}m` : ""}`;}
  return `${person.online ? "Online" : "Offline"} ${duration}`;
}

function Presence({ person, asOf }) {
  const label = presenceLabel(person, asOf);
  return <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-600 tw-whitespace-nowrap"
    title="Online periods join recorded activity separated by no more than 10 minutes. Checked every 60 seconds while this page is visible.">
    <span aria-hidden="true" className={`tw-inline-block tw-w-2 tw-h-2 tw-rounded-full ${person.online ? "tw-bg-green-500" : "tw-bg-gray-400"}`} />
    {label}
  </span>;
}

const sessionEventStyles = {
  "Logged in": { label: "Login", color: "#15803d", shape: "circle" },
  "Signed out": { label: "Logout", color: "#b91c1c", shape: "square" },
  "Timed out": { label: "Timeout", color: "#b45309", shape: "diamond" },
  "Session ended": { label: "Session ended", color: "#64748b", shape: "square" },
};

function timelineSessionEvents(detail) {
  const start = new Date(detail?.activityTimeline?.start).getTime();
  const end = new Date(detail?.activityTimeline?.end).getTime();
  return (detail?.recentActivity || []).filter((event) => {
    const at = new Date(event.at).getTime();
    return /^(login|logout)-/.test(event.id) && sessionEventStyles[event.action] && at >= start && at < end;
  }).map((event) => ({
    ...event, ...sessionEventStyles[event.action],
    position: (new Date(event.at).getTime() - start) / (end - start) * 100,
    time: new Date(event.at).toLocaleTimeString("en-US", {
      hour: "numeric", minute: "2-digit", second: "2-digit", timeZone: "America/New_York", timeZoneName: "short",
    }),
  })).sort((a, b) => new Date(a.at) - new Date(b.at) || a.id.localeCompare(b.id));
}

function ActivityTimeline({ detail, loading, error }) {
  const timeline = !loading && !error && detail?.activityTimeline ? detail.activityTimeline : null;
  const segments = Array.isArray(timeline?.segments) ? timeline.segments : null;
  const pages = Array.isArray(timeline?.pages) ? timeline.pages : null;
  const start = new Date(timeline?.start).getTime();
  const dayMinutes = timeline ? (new Date(timeline.end).getTime() - start) / 60000 : 1440;
  const label = (minute) => Number.isFinite(start)
    ? new Date(start + minute * 60000).toLocaleTimeString("en-US", {
      hour: "numeric", minute: "2-digit", timeZone: "America/New_York", ...dayMinutes !== 1440 && { timeZoneName: "short" },
    })
    : `${Math.floor(minute / 60) % 12 || 12}${minute >= 720 && minute < 1440 ? "pm" : "am"}`;
  const hourLabel = (minute) => {
    const clock = label(minute);
    const showZone = dayMinutes === 1500 && clock.startsWith("1:");
    return (showZone ? clock : clock.replace(/ E[DS]T$/, "")).replace(":00", "").replace(" AM", "a").replace(" PM", "p");
  };
  const pct = (minute) => `${Math.max(0, Math.min(dayMinutes, minute)) / dayMinutes * 100}%`;
  const activeMinutes = (segments || []).reduce((total, span) => total + span.endMinute - span.startMinute, 0);
  const legend = pages?.length ? Object.keys(timelineCategoryColors) : null;
  const sessionEvents = timeline ? timelineSessionEvents(detail) : [];
  const hoursInDay = dayMinutes / 60;
  const workMarkers = timeline ? monitoringMarkers({ day: detail.day, ...timeline }) : [];
  const gaps = [];
  for (let i = 1; i < (segments || []).length; i++) {
    const start = segments[i - 1].endMinute, end = segments[i].startMinute;
    if (end - start >= 1) { gaps.push({ start, end }); }
  }
  const gapLength = (g) => {
    const minutes = Math.round(g.end - g.start);
    return minutes < 60 ? `${minutes}m` : `${Math.floor(minutes / 60)}h ${minutes % 60 ? `${minutes % 60}m` : ""}`.trim();
  };
  return <div className="tw-px-4 tw-pt-3 tw-pb-1">
    <div className="tw-flex tw-justify-between tw-text-xs tw-text-gray-600 tw-mb-1">
      <span>Activity · {hoursInDay} hours ET</span>
      <span>{loading ? "Loading…" : segments == null ? "Unavailable" : segments.length ? "Engaged" : "No activity this day"}</span>
    </div>
    <div className="tw-overflow-x-auto" tabIndex={0} aria-label="Hourly activity timeline">
      <div style={{ minWidth: dayMinutes === 1440 ? 704 : 900, padding: "12px 16px 0" }}>
        <div className="tw-relative tw-h-3">
          <div role="img" aria-label={`Activity, full ET calendar day: ${segments == null ? "unavailable" : `${Math.round(activeMinutes)} minutes engaged, including gaps up to 5 minutes; page visits colored by activity category`}`}
            className="tw-absolute tw-inset-0 tw-rounded-sm tw-bg-gray-200 tw-overflow-hidden">
            {Array.from({ length: hoursInDay - 1 }, (_, i) => i + 1).map((hour) => <span key={hour} aria-hidden="true"
              className="tw-absolute tw-h-full tw-border-l tw-border-white" style={{ left: pct(hour * 60) }} />)}
            {(segments || []).map((span, i) => <span key={i} title={`${label(span.startMinute)} – ${label(span.endMinute)}`}
              className="tw-absolute tw-h-full tw-bg-green-600"
              style={{ left: pct(span.startMinute), width: `${parseFloat(pct(span.endMinute)) - parseFloat(pct(span.startMinute))}%` }} />)}
            {(pages || []).map((span, i) => <span key={`p${i}`} title={`${label(span.startMinute)} – ${label(span.endMinute)} · ${span.category}`}
              className="tw-absolute tw-h-full"
              style={{ left: pct(span.startMinute), width: `${parseFloat(pct(span.endMinute)) - parseFloat(pct(span.startMinute))}%`, background: timelineCategoryColors[span.category] || timelineCategoryColors.other, minWidth: 1 }} />)}
            {gaps.map((gap, i) => <span key={`gap${i}`} aria-hidden="true"
              title={`Break ${label(gap.start)} – ${label(gap.end)} (${gapLength(gap)})`}
              className="tw-absolute tw-inset-y-0 tw-cursor-help"
              style={{ left: pct(gap.start), width: `${Math.max(0.3, (gap.end - gap.start) / dayMinutes * 100)}%` }} />)}
            {workMarkers.map((marker) => <span key={marker.label} title={`${marker.label} ET`} aria-hidden="true"
              className="tw-absolute tw-inset-y-0 tw-z-10 tw-border-l-2 tw-border-dashed tw-border-blue-900" style={{ left: `${marker.position}%` }} />)}
          </div>
          {sessionEvents.map((event) => <span key={event.id} role="img" tabIndex={0}
            aria-label={`${event.label} at ${event.time}`} title={`${event.label} · ${event.time}`}
            className="tw-absolute tw-z-20 tw-rounded-sm focus:tw-ring-2 focus:tw-ring-blue-500"
            style={{ left: `${event.position}%`, top: -10, height: 34, width: 16, transform: "translateX(-50%)" }}>
            <svg viewBox="0 0 16 34" width="16" height="34" aria-hidden="true">
              <path d="M8 5V33" stroke="white" strokeWidth="4" />
              <path d="M8 5V33" stroke={event.color} strokeWidth="2" />
              {event.shape === "circle" ? <circle cx="8" cy="5" r="4" fill={event.color} />
                : event.shape === "diamond" ? <path d="M8 0L13 5L8 10L3 5Z" fill={event.color} />
                  : <rect x="4" y="1" width="8" height="8" rx="1" fill={event.color} />}
            </svg>
          </span>)}
        </div>
        <div className="tw-relative tw-h-5 tw-text-gray-500" style={{ marginTop: 14, fontSize: dayMinutes === 1440 ? 10 : 9, fontVariantNumeric: "tabular-nums" }} aria-hidden="true">
          {Array.from({ length: hoursInDay + 1 }, (_, hour) => <span key={hour} className="tw-absolute tw-whitespace-nowrap"
            style={{ left: pct(hour * 60), transform: "translateX(-50%)" }}>
            {hourLabel(hour * 60)}
          </span>)}
        </div>
      </div></div>
    {!loading && !error && timeline && <div className="tw-mt-2">
      {sessionEvents.length ? <ol aria-label="Login and session-ending times" className="tw-list-none tw-m-0 tw-p-0 tw-flex tw-flex-wrap tw-gap-2">
        {sessionEvents.map((event) => <li key={event.id} className="tw-flex tw-items-center tw-gap-1 tw-rounded tw-border tw-border-gray-200 tw-bg-gray-50 tw-px-2 tw-py-1 tw-text-xs"
          style={{ borderLeft: `3px solid ${event.color}` }}>
          <strong style={{ color: event.color }}>{event.label}</strong>
          <time dateTime={event.at} style={{ fontVariantNumeric: "tabular-nums" }}>{event.time}</time>
        </li>)}
      </ol> : <p className="tw-text-xs tw-text-gray-500 tw-mb-0">No login or session-ending times recorded for this day.</p>}
    </div>}
    {legend && <div className="tw-flex tw-flex-wrap tw-gap-x-3 tw-mt-2" style={{ fontSize: 10 }}>
      {legend.map((category) => <span key={category} className="tw-text-gray-600 tw-capitalize">
        <span aria-hidden="true" className="tw-inline-block tw-w-2 tw-h-2 tw-rounded-sm tw-mr-0.5" style={{ background: timelineCategoryColors[category] }} />
        {category === "home" ? "home / nav" : category}
      </span>)}
    </div>}
  </div>;
}

function recentActivityWhen(timestamp, asOf, detailed = false) {
  const at = new Date(timestamp);
  const now = asOf || new Date();
  if (!Number.isFinite(at.getTime())) {return "Time unavailable";}
  const parts = (date) => Object.fromEntries(etActivityDate.formatToParts(date).map(({ type, value }) => [type, value]));
  const seen = parts(at);
  const today = parts(now);
  const dayOffset = (Date.UTC(today.year, today.month - 1, today.day) - Date.UTC(seen.year, seen.month - 1, seen.day)) / 86400000;
  const date = at.toLocaleDateString("en-US", {
    timeZone: "America/New_York", month: "short", day: "numeric", ...seen.year !== today.year && { year: "numeric" },
  });
  const day = dayOffset === 0 ? "" : dayOffset <= 7 ? `${seen.weekday} · ` : `${date} · `;
  const clock = detailed ? at.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", second: "2-digit", timeZone: "America/New_York" }) : timeET(at);
  return `${day}${clock} ET`;
}

function RecentActivity({ events, asOf, loading, error, dayLabel }) {
  const recent = Array.isArray(events) ? events : [];
  return <div className="tw-px-4 tw-py-3 tw-border-t tw-border-gray-200">
    <div className="tw-flex tw-justify-between tw-items-baseline tw-gap-2 tw-mb-2">
      <h3 className="tw-text-sm tw-font-semibold tw-m-0">Recent activity · {dayLabel}</h3>
      {!loading && !error && <span className="tw-text-xs tw-text-gray-500">{recent.length} events</span>}
    </div>
    {loading ? <p role="status" className="tw-text-xs tw-text-gray-500">Loading this day&apos;s activity…</p>
      : error ? <p role="alert" className="tw-text-xs tw-text-red-700">{error}</p>
        : !recent.length ? <p className="tw-text-xs tw-text-gray-500 tw-mb-0">No activity recorded for this day.</p>
          : <ol aria-label={`All recorded activity for ${dayLabel}`} tabIndex={0} className="tw-m-0 tw-p-0 tw-list-none tw-overflow-y-auto tw-divide-y tw-divide-gray-100"
            style={{ maxHeight: 320, overscrollBehavior: "contain" }}>
            {recent.map((event, index) => {
              const link = event.page?.startsWith("/") && !event.page.startsWith("//") ? event.page
                : event.policyNo ? `/inspections/${encodeURIComponent(event.policyNo)}/process/pre_r` : null;
              return <li key={event.id || `${event.at}-${index}`} className="tw-py-2 tw-pr-2 tw-text-xs">
                <div className="tw-flex tw-justify-between tw-items-baseline tw-gap-3">
                  <strong className="tw-font-medium tw-text-gray-800">{event.action}</strong>
                  <time className="tw-flex-shrink-0 tw-text-gray-500" dateTime={event.at}>{recentActivityWhen(event.at, asOf, true)}</time>
                </div>
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-mt-1">
                  {link && <a href={link} className="tw-text-primary hover:tw-underline tw-break-all">{event.policyNo || event.page}</a>}
                  {event.durationSeconds > 0 && <span className="tw-text-gray-500">{monitoringDuration(event.durationSeconds)}</span>}
                  {event.textKind && <span className={`tw-rounded tw-px-1.5 tw-py-0.5 ${event.inferred ? "tw-bg-amber-50 tw-text-amber-900" : "tw-bg-gray-100 tw-text-gray-700"}`}
                    title={event.inferred ? "No Foursite activity recorded in the preceding 10 minutes; may be scheduled or sent directly in Quo. No working time is added." : ""}>
                    {schedulerTextLabels[event.textKind] || "Unknown source"}{event.inferred ? " · inferred" : ""}
                  </span>}
                </div>
              </li>;
            })}
          </ol>}
  </div>;
}

function PersonDetail({ person, detail, loading, error, onClose, asOf, date, today, onDayChange }) {
  const name = schedulerDisplayName(person);
  const isToday = date === today;
  const dayLabel = isToday ? "Today" : monitoringDateLabel(date, { weekday: "short", year: "numeric" });
  const day = detail?.days?.[0];
  const stats = isToday ? person : detail;
  const inClock = stats && loggedInSpan(stats, asOf);
  const metrics = [
    ["Signed-in time", inClock ? clockDuration(inClock.ms) : stats ? "Unavailable" : "—"],
    ["Engaged time", stats ? engagedTime(stats.engagedHours) : "—"],
    ["Scheduling time", stats ? engagedTime(stats.schedulingHours) : "—"],
    ["Call talk time", stats ? hours(stats.callHours) : "—"],
    ["Policies scheduled", stats?.scheduled],
    ["Texts sent", stats?.texts],
    ["Calls / missed", stats ? `${stats.calls} / ${stats.missed}` : "—"],
    ["Reports saved", stats?.reportEdits],
  ];
  return <section aria-label={`${name} · ${dayLabel}`} aria-busy={loading} className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-shadow-lg tw-overflow-y-auto"
    style={{ maxHeight: "calc(100vh - 220px)" }}>
    <div className="tw-flex tw-items-center tw-gap-3 tw-p-4 tw-bg-blue-50 tw-border-b tw-border-gray-200">
      {stats ? <Cookie person={{ ...stats, online: isToday && person.online }} />
        : <span aria-hidden="true" className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-gray-200 tw-flex-shrink-0" />}
      <div className="tw-flex-1 tw-min-w-0"><strong className="tw-text-sm">{name}</strong>
        <div>{isToday && <Presence person={person} asOf={asOf} />} <span className="tw-text-xs tw-text-gray-500">{isToday ? "· Today ET" : "Historical activity · ET"}</span></div></div>
      <button type="button" onClick={onClose} aria-label="Close scheduler details" className={button}>✕</button>
    </div>
    <nav aria-label="Activity day" className="tw-flex tw-items-center tw-justify-between tw-gap-1 tw-px-3 tw-py-2 tw-border-b tw-border-gray-200">
      <button type="button" aria-label="Previous activity day" disabled={date <= monitoringStart}
        className={`${button} tw-text-blue-900 disabled:tw-opacity-40`} onClick={() => onDayChange(shiftMonitoringDate(date, -1))}>‹</button>
      <span aria-live="polite" className="tw-text-xs tw-font-semibold tw-text-center">{dayLabel} · ET</span>
      <div className="tw-flex tw-items-center tw-gap-1">
        <button type="button" disabled={isToday} onClick={() => onDayChange("")}
          className={`${button} tw-text-blue-900 disabled:tw-opacity-40`}>Today</button>
        <button type="button" aria-label="Next activity day" disabled={isToday}
          className={`${button} tw-text-blue-900 disabled:tw-opacity-40`} onClick={() => onDayChange(shiftMonitoringDate(date, 1))}>›</button>
      </div>
    </nav>
    <ActivityTimeline detail={detail} loading={loading} error={error} />
    <dl className="tw-px-4 tw-py-2">
      {metrics.map(([label, value]) => <div key={label} className="tw-flex tw-justify-between tw-gap-4 tw-py-2 tw-border-b tw-border-gray-100 tw-text-sm">
        <dt className="tw-text-gray-600">{label}</dt><dd className="tw-font-semibold tw-m-0" style={{ fontVariantNumeric: "tabular-nums" }}>{value ?? "—"}</dd>
      </div>)}
    </dl>
    <div className="tw-p-4 tw-pt-1 tw-text-xs tw-text-gray-600" aria-live="polite">
      {loading ? "Loading activity times…" : error || (day
        ? <div className="tw-flex tw-justify-between">
          <span>First activity <strong>{timeET(new Date(day.first))} ET</strong></span>
          <span>Latest <strong>{timeET(new Date(day.last))} ET</strong></span>
        </div>
        : "No recorded activity times for this day.")}
    </div>
    <div className="tw-px-4 tw-pb-3">{stats && <SchedulerTextBreakdown counts={stats.textKinds} />}</div>
    <RecentActivity events={detail?.recentActivity} asOf={asOf} dayLabel={dayLabel} loading={loading && !detail} error={error} />
  </section>;
}

export default function SchedulerHeaderPrototype() {
  const [people, setPeople] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedDay, setSelectedDay] = useState("");
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [asOf, setAsOf] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [popoverLeft, setPopoverLeft] = useState(0);
  const header = useRef(null);
  const strip = useRef(null);
  const requestPending = useRef(false);
  const today = monitoringToday(asOf || new Date());
  const date = selectedDay || today;

  useEffect(() => {
    let cancelled = false;
    requestPending.current = true;
    setLoading(true);
    setError("");
    apiReports.getSchedulerActivityToday().then((response) => {
      if (response.data.status !== 200) {throw new Error("Unable to load today's activity.");}
      if (!cancelled) {
        setPeople((response.data.result.schedulers || []).slice().sort((a, b) => schedulerDisplayName(a).localeCompare(schedulerDisplayName(b))));
        const stamp = new Date(response.data.time);
        setAsOf(Number.isFinite(stamp.getTime()) ? stamp : new Date());
      }
    }).catch(() => {
      if (!cancelled) {
        setPeople([]);
        setSelected(null);
        setAsOf(null);
        setError("Today's activity could not be loaded.");
      }
    }).finally(() => {
      if (!cancelled) {requestPending.current = false; setLoading(false);}
    });
    return () => { cancelled = true; };
  }, [revision]);

  useEffect(() => {
    const update = () => {
      if (!document.hidden && !requestPending.current) {setRevision((v) => v + 1);}
    };
    const timer = window.setInterval(update, 60000);
    document.addEventListener("visibilitychange", update);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", update);
    };
  }, []);

  useEffect(() => {
    if (selected == null) {return;}
    let cancelled = false;
    setDetail((current) => current?.userId === selected && current?.day === date ? current : null);
    setDetailLoading(true);
    setDetailError("");
    apiReports.getSchedulerActivityDay(selected, date).then((response) => {
      if (response.data.status !== 200) {throw new Error("Unable to load activity times.");}
      if (!cancelled) {setDetail(response.data.result);}
    }).catch(() => {
      if (!cancelled) {setDetailError("Activity times could not be loaded.");}
    }).finally(() => { if (!cancelled) {setDetailLoading(false);} });
    return () => { cancelled = true; };
  }, [selected, date, revision]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") {setSelected(null);}
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useLayoutEffect(() => {
    if (selected == null) {return;}
    const scroller = strip.current;
    const reposition = () => {
      const anchor = scroller.querySelector('[aria-expanded="true"]');
      if (!anchor) {return;}
      const parent = header.current.getBoundingClientRect();
      const bounds = anchor.getBoundingClientRect();
      const width = Math.min(760, parent.width, window.innerWidth - 16);
      const min = Math.max(0, 8 - parent.left);
      const max = Math.max(min, Math.min(parent.width, window.innerWidth - 8 - parent.left) - width);
      setPopoverLeft(Math.max(min, Math.min(bounds.left - parent.left, max)));
    };
    reposition();
    scroller.addEventListener("scroll", reposition, { passive: true });
    window.addEventListener("resize", reposition);
    return () => {
      scroller.removeEventListener("scroll", reposition);
      window.removeEventListener("resize", reposition);
    };
  }, [selected]);

  const person = people.find((p) => p.userId === selected);
  const selectedDetail = detail?.userId === selected && detail?.day === date ? detail : null;
  const detailPanel = person ? <PersonDetail key={selected} person={person}
    detail={selectedDetail} loading={detailLoading || !selectedDetail && !detailError}
    error={detailError} onClose={() => setSelected(null)} asOf={asOf} date={date} today={today} onDayChange={setSelectedDay} /> : null;

  return <section aria-label="Scheduler activity" className="tw-relative tw-my-4 tw-text-gray-800">
    <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-mb-2">
      <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs">
        <span>Today ET · {asOf ? `updated ${timeET(asOf)} ET` : loading ? "loading" : "unavailable"} · auto 60s</span>
      </div>
      <div className="tw-flex tw-gap-2">
        <button type="button" disabled={loading} className={`${button} tw-bg-blue-50 tw-text-blue-900`} onClick={() => setRevision((v) => v + 1)}>
          {loading ? "Loading…" : "Update activity"}
        </button>
      </div>
    </div>
    {error && <p role="alert" className="tw-text-red-700 tw-p-3 tw-bg-red-50">{error}</p>}
    {!loading && !error && !people.length && <p className="tw-p-3 tw-bg-gray-50">No schedulers configured for this company.</p>}

    <div ref={header} className="tw-relative">
      <div className="tw-flex tw-items-stretch tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-sm">
        <div className="tw-flex tw-items-center tw-px-4 tw-text-sm tw-font-semibold tw-border-r tw-border-gray-200">Scheduler<br />activity</div>
        <button type="button" className="tw-px-2 tw-text-blue-800" aria-label="Previous schedulers"
          onClick={() => strip.current.scrollBy({ left: -260, behavior: "smooth" })}>‹</button>
        <div ref={strip} className="tw-flex tw-flex-1 tw-overflow-x-auto tw-py-1">
          {people.map((p) => <button key={p.userId} type="button" onClick={() => { setSelectedDay(""); setSelected(selected === p.userId ? null : p.userId); }}
            aria-expanded={selected === p.userId} aria-label={`${schedulerDisplayName(p)}: ${engagedTime(p.engagedHours)} engaged,${(() => { const span = loggedInSpan(p, asOf); return span ? ` in ${clockDuration(span.ms)},` : ""; })()} ${presenceLabel(p, asOf)}, show details`}
            className={`tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-2 tw-border-r tw-border-gray-100 tw-text-left tw-flex-shrink-0
              focus:tw-ring-2 focus:tw-ring-inset focus:tw-ring-blue-500 ${selected === p.userId ? "tw-bg-blue-50" : "hover:tw-bg-gray-50"}`}>
            <Cookie person={p} />
            <div><strong className="tw-block tw-text-sm tw-truncate" title={schedulerDisplayName(p)} style={{ maxWidth: 125 }}>{schedulerDisplayName(p)}</strong>
              <Presence person={p} asOf={asOf} /></div>
            <div className="tw-ml-auto tw-text-right tw-whitespace-nowrap tw-flex tw-flex-col tw-justify-center tw-pl-2">
              {(() => { const span = loggedInSpan(p, asOf);
                return span ? <span className="tw-text-xs tw-text-gray-500"
                  title={span.fallback ? "Elapsed since first recorded activity today (session timing unavailable)" : "Total signed-in time today; logged-out gaps and overlapping sessions are excluded"}>In {clockDuration(span.ms)}</span> : null; })()}
              <strong className="tw-text-sm">{engagedTime(p.engagedHours)}</strong>
            </div>
          </button>)}
        </div>
        <button type="button" className="tw-px-2 tw-text-blue-800" aria-label="Next schedulers"
          onClick={() => strip.current.scrollBy({ left: 260, behavior: "smooth" })}>›</button>
      </div>
      {detailPanel && <div className="tw-absolute tw-top-full tw-mt-2 tw-z-30"
        style={{ left: popoverLeft, width: "min(760px, 100%, calc(100vw - 16px))" }}>{detailPanel}</div>}
    </div>
  </section>;
}
